<script setup lang="ts">
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import utils from '@/utils';
import {
  LeadsFormComponentItem,
  LeadsFormData,
  LeadsFormProps,
} from '@/types/components/leads-form';
import DynamicForm from '@/form/DynamicForm.vue';
import SiteRichText from '@/elements/SiteRichText.vue';
import { LoadingProvider, ToastProvider } from '@/types/layout';
import { DynamicFormRef } from '@/types/form/dynamic-form';
import api from '@/api';
import componentFactory from '@/temp/componentFactory';
import {
  applyDynamicFormAutofill,
  isCountryField,
  isStateField,
  updateRegionalFieldsByCountry,
} from '@/utils/dynamic-form-autofill';
import { getOnlineMessageTracking } from '@/utils/online-message';
import { omitDynamicFormCheckboxValues } from '@/utils/dynamic-form-draft';

const props = defineProps<LeadsFormProps>();
const loading = inject<LoadingProvider>('loading')!;
const toast = inject<ToastProvider>('toast')!;
const formRef = ref<DynamicFormRef>();
const captchaValue = ref('');
const captchaError = ref('');
const captchaLoading = ref(false);
const captchaImage = ref('');
const validateCode = ref('');
const codeToken = ref('');
const isSubmitted = ref(false);
const submitting = ref(false);
const currentCountryValue = ref('');
const FORM_DRAFT_STORAGE_PREFIX = 'onlineMessageFormDraft';
const formDraftStorageKey = ref('');
const isRestoringFormDraft = ref(false);
let formInitializationId = 0;
const rightComponents = computed(() =>
  (props.fields?.rightComponents ?? []).map((componentData) => ({
    ...componentData,
    componentName: getComponentName(componentData),
  }))
);
const rootClass = computed(() => {
  const result = utils.overall.baseComClass(props.fields, 'c-leads-form');
  const [desktopDirection] = utils.sp.getValByFields(props.fields, 'desktopDirection');
  console.log('desktopLayout', desktopDirection);
  if (!desktopDirection || ['row', 'row-reverse'].includes(desktopDirection)) {
    result.push('row');
  }
  return result;
});
const rootStyle = computed(() => {
  const result = utils.overall.baseComStyle(props.fields);
  const [desktopDirection] = utils.sp.getValByFields(props.fields, 'desktopDirection');
  if (desktopDirection) {
    result['--flex-direction'] = desktopDirection;
    if (['column', 'column-reverse'].includes(desktopDirection)) {
      result['--align'] = 'left';
    }
  }
  return result;
});
const normalizedBackMedia = computed(() => {
  if (!props.fields?.backMedia?.fields) return props.fields?.backMedia;

  const normalizeImageField = (imageField: any) => {
    const src = imageField?.value?.src;
    if (
      !src ||
      (!src.startsWith('http://') && !src.startsWith('https://') && !src.startsWith('//'))
    ) {
      return imageField;
    }

    const url = new URL(src.startsWith('//') ? `https:${src}` : src);
    return {
      ...imageField,
      value: {
        ...imageField.value,
        src: `${url.pathname}${url.search}${url.hash}`,
      },
    };
  };

  return {
    ...props.fields.backMedia,
    fields: {
      ...props.fields.backMedia.fields,
      desktopImage: normalizeImageField(props.fields.backMedia.fields.desktopImage),
      mobileImage: normalizeImageField(props.fields.backMedia.fields.mobileImage),
    },
  };
});
function getComponentName(componentData?: LeadsFormComponentItem) {
  return `${
    componentData?.componentName || componentData?.templateName || componentData?.template || ''
  }`.replace(/\s+/g, '');
}
function resolveDynamicComponent(componentData?: LeadsFormComponentItem) {
  const componentName = getComponentName(componentData);
  return componentFactory(componentName) || componentName;
}
function getDynamicComponentProps(componentData: LeadsFormComponentItem) {
  return {
    fields: componentData?.fields ?? {},
    rendering: componentData,
  };
}
const normalizeCaptchaData = (data: any = {}) => {
  validateCode.value = data.validateCode || data.validatecode || '';
  codeToken.value = data.codeToken || data.code_token || '';
  captchaImage.value = data.src || '';
};

const getCookie = (name: string) => {
  const item = document.cookie.split('; ').find((cookie) => cookie.startsWith(`${name}=`));
  return item ? decodeURIComponent(item.split('=').slice(1).join('=')) : '';
};

const getSubmitHeaders = () => {
  const session = getCookie('GSAccessSession');
  return {
    Referer: window.location.href,
    ...(session ? { GSAccessSession: session } : {}),
  };
};

const getErrorMessage = (error: any, fallback: string) => {
  return error?.response?.data?.message || error?.message || error?.data?.message || fallback;
};

const refreshCaptcha = async () => {
  if (captchaLoading.value) return;

  captchaLoading.value = true;
  captchaError.value = '';
  const [data, error] = await api.common.getValidateCode();
  captchaLoading.value = false;

  if (error) {
    captchaImage.value = '';
    captchaError.value = getErrorMessage(error, 'Failed to load the security code.');
    return;
  }

  normalizeCaptchaData(data);
  captchaValue.value = '';
};
const onCaptchaInput = () => {
  captchaError.value = '';
};
const getHomeUrl = () => {
  const { pathname, origin } = window.location;
  const langMatch = pathname.match(/^\/([a-z]{2}-[A-Z]{2})(?:\/|$)/);
  return `${origin}${langMatch ? `/${langMatch[1]}` : ''}/`;
};
const goHome = () => {
  window.location.href = getHomeUrl();
};
const validateCaptcha = () => {
  if (captchaValue.value.trim() && codeToken.value) {
    captchaError.value = '';
    return true;
  }
  captchaError.value = 'Please enter the security code shown on the right.';
  return false;
};
const getCurrentCountryValue = () => {
  const values = formRef.value?.getValues?.() || {};
  return (
    values.country || values.countryRegion || values.countryCode || values.country_region || ''
  );
};

const getRegionalDraftValues = (values: Record<string, any>) => ({
  country:
    values.country || values.countryRegion || values.countryCode || values.country_region || '',
  state: values.state || values.State || '',
});

const persistFormDraft = () => {
  if (
    isSubmitted.value ||
    isRestoringFormDraft.value ||
    !formDraftStorageKey.value ||
    !formRef.value
  ) {
    return;
  }

  try {
    const draftValues = omitDynamicFormCheckboxValues(
      props.fields?.form,
      formRef.value.getValues()
    );
    window.localStorage.setItem(formDraftStorageKey.value, JSON.stringify(draftValues));
  } catch {
    // Ignore unavailable storage and values that cannot be serialized.
  }
};

const restoreFormDraft = () => {
  if (!formDraftStorageKey.value || !formRef.value) return;

  try {
    const storedDraft = window.localStorage.getItem(formDraftStorageKey.value);
    const values = storedDraft ? JSON.parse(storedDraft) : null;
    if (!values || typeof values !== 'object' || Array.isArray(values)) return;
    const draftValues = omitDynamicFormCheckboxValues(props.fields?.form, values);

    // Rewrite old drafts so previously cached checkbox values are removed immediately.
    window.localStorage.setItem(formDraftStorageKey.value, JSON.stringify(draftValues));

    formRef.value.updateFields(
      Object.entries(draftValues).reduce<Record<string, { value: any }>>(
        (updates, [field, value]) => {
          updates[field] = { value };
          return updates;
        },
        {}
      )
    );
    const regionalDraft = getRegionalDraftValues(draftValues);
    currentCountryValue.value = regionalDraft.country;
    updateRegionalFieldsByCountry(formRef.value, regionalDraft.country, regionalDraft.state);
  } catch {
    // Ignore malformed drafts so users can still use the form.
  }
};

const clearFormDraft = () => {
  if (!formDraftStorageKey.value) return;
  window.localStorage.removeItem(formDraftStorageKey.value);
};

const onDocumentInput = (event: Event) => {
  const target = event.target as HTMLElement | null;
  if (target?.closest('.c-leads-form')) {
    void nextTick().then(persistFormDraft);
  }
};

const initializeForm = async () => {
  const initializationId = ++formInitializationId;
  persistFormDraft();
  const pathname = window.location.pathname.replace(/\/+$/, '') || '/';
  formDraftStorageKey.value = `${FORM_DRAFT_STORAGE_PREFIX}:${pathname}`;
  isRestoringFormDraft.value = true;
  await nextTick();
  await applyDynamicFormAutofill(formRef.value);
  if (initializationId !== formInitializationId) return;
  restoreFormDraft();
  currentCountryValue.value = getCurrentCountryValue();
  isRestoringFormDraft.value = false;
};

const onFormChange = (_fieldType: string, fieldName: string, value: string) => {
  if (isCountryField(fieldName)) {
    currentCountryValue.value = value || '';
    updateRegionalFieldsByCountry(formRef.value, value, undefined, true);
  } else if (isStateField(fieldName)) {
    updateRegionalFieldsByCountry(
      formRef.value,
      currentCountryValue.value || getCurrentCountryValue(),
      value
    );
  }

  void nextTick().then(persistFormDraft);
};

const escapeHtml = (value: unknown) =>
  `${value ?? ''}`
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatEmailBody = (formData: Record<string, any>) =>
  Object.entries(formData)
    .filter(([fieldName]) => fieldName !== 'externalData')
    .map(([fieldName, value]) => {
      const displayValue = Array.isArray(value) ? value.join(', ') : value;
      return `<p><strong>${escapeHtml(fieldName)}</strong>: ${escapeHtml(displayValue)}</p> <br />`;
    })
    .join('');

const onSubmit = async () => {
  if (submitting.value || !formRef.value) return;
  const formRes = await formRef.value.validate<LeadsFormData>();
  if (!formRes.valid) {
    formRef.value.focusFirstInvalid?.();
    return;
  }
  if (!formRes.data) return;
  if (!validateCaptcha()) return;

  submitting.value = true;
  loading.show();
  try {
    const formType = props.fields?.formType;
    const hasFormType = Boolean(formType?.fields);
    const [, error] = hasFormType
      ? await api.contact.sendCustomerEmail(
          null,
          {
            subject: formType?.fields?.text?.value || '',
            body: formatEmailBody(formRes.data),
            type: formType?.fields?.phrase?.value || '',
          },
          { headers: getSubmitHeaders() }
        )
      : await api.downloadResource.submitOnlineMessage(
          null,
          {
            ...formRes.data,
            ...getOnlineMessageTracking(),
            validateCode: captchaValue.value.trim(),
            codeToken: codeToken.value,
          },
          { headers: getSubmitHeaders() }
        );

    if (error) {
      void toast.showError(getErrorMessage(error, 'Submission failed. Please try again.'));
      await refreshCaptcha();
      return;
    }

    isSubmitted.value = true;
    const successUrl = new URL(window.location.href);
    successUrl.search = '?success';
    window.history.replaceState(window.history.state, '', successUrl.toString());
    clearFormDraft();
    captchaValue.value = '';
    await refreshCaptcha();
  } finally {
    submitting.value = false;
    loading.hide();
  }
};
onMounted(() => {
  refreshCaptcha();
  void initializeForm();
  window.addEventListener('pagehide', persistFormDraft);
  window.addEventListener('beforeunload', persistFormDraft);
  document.addEventListener('input', onDocumentInput, true);
});

watch(
  () => props.fields?.form,
  () => {
    void initializeForm();
  }
);

onBeforeUnmount(() => {
  persistFormDraft();
  window.removeEventListener('pagehide', persistFormDraft);
  window.removeEventListener('beforeunload', persistFormDraft);
  document.removeEventListener('input', onDocumentInput, true);
});
</script>

<template>
  <div :class="rootClass" :style="rootStyle" v-if="fields">
    <div v-if="normalizedBackMedia" class="c-leads-form__media">
      <AdaptiveImage
        :field="normalizedBackMedia"
        class="c-leads-form__media-image"
        background-position="right bottom"
        :is-background="true"
        :lazy-load="false"
        :enable-scroll-detection="false"
        :show="true"
        :show-image-redius="false"
      />
    </div>
    <content-grid
      :desktop-default-grid="utils.gs.getGridRange(fields.desktopGrid)"
      :mobile-default-grid="[1, 14]"
    >
      <div
        :class="['c-leads-form__content', { 'has-right': !isSubmitted && rightComponents.length }]"
      >
        <div v-if="isSubmitted" class="c-leads-form__success">
          <div class="c-leads-form__success-icon" aria-hidden="true">
            <svg viewBox="0 0 32 32" focusable="false">
              <path d="M8.5 16.5 13.5 21.5 24 10.5" />
            </svg>
          </div>
          <SiteRichText :field="fields.successTitle" class="c-leads-form__success-title" />
          <SiteRichText :field="fields.successBody" class="c-leads-form__success-body" />
          <button class="c-leads-form__success-button" type="button" @click="goHome">
            Return To Homepage
          </button>
        </div>
        <div v-else class="c-leads-form__form-column">
          <div class="c-leads-form__side">
            <h2 v-if="fields?.title?.value" v-html="fields?.title.value" />
            <SiteRichText :field="fields.body" class="c-leads-form__body" />
            <SiteRichText :field="fields.tips" class="c-leads-form__tips" />
          </div>
          <div class="c-leads-form__main" @input="persistFormDraft">
            <DynamicForm :form="fields.form" ref="formRef" @change="onFormChange" />
            <div class="c-leads-form__captcha">
              <div :class="['c-leads-form__captcha-field', { 'has-error': Boolean(captchaError) }]">
                <input
                  v-model="captchaValue"
                  type="text"
                  inputmode="text"
                  autocomplete="off"
                  autocapitalize="none"
                  spellcheck="false"
                  placeholder="security code on the right"
                  aria-label="Security code"
                  @input="onCaptchaInput"
                />
              </div>
              <img
                class="c-leads-form__captcha-image"
                :class="{ 'is-loading': captchaLoading }"
                :src="captchaImage"
                alt="Security code"
                role="button"
                @click="refreshCaptcha"
              />
              <p v-if="captchaError" class="c-leads-form__captcha-error">
                {{ captchaError }}
              </p>
            </div>
            <SiteButton
              v-bind="fields.submitButton"
              @click="onSubmit"
              class="c-leads-form__button"
              :disabled="submitting"
              :loading="submitting"
            />
          </div>
        </div>
        <aside v-if="!isSubmitted && rightComponents.length" class="c-leads-form__right">
          <component
            :is="resolveDynamicComponent(componentData)"
            v-for="(componentData, componentIndex) in rightComponents"
            v-bind="getDynamicComponentProps(componentData)"
            :key="componentData?.uid ?? componentData?.id ?? `right-component-${componentIndex}`"
          />
        </aside>
      </div>
    </content-grid>
  </div>
</template>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';
.c-leads-form {
  $c: &;
  position: relative;
  background-color: var(--back-color, $white);
  color: var(--color, $black);
  padding: 60px 0;
  &__media {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }
  &__media-image {
    width: 100%;
    height: 100%;
    > div,
    .e-background-image,
    .e-background-image__img,
    .background-image {
      width: 100%;
      height: 100%;
    }
    .e-background-image__img.background-image > img,
    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      object-position: right bottom !important;
    }
  }
  &__title {
    margin-bottom: 12px;
  }
  &__content {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 40px;
  }
  &__side {
    text-align: left;
  }
  &__form-column {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  &__main {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
    .e-site-button {
      margin-top: 14px;
      // align-self: center;
    }
  }
  &__captcha {
    display: grid;
    grid-template-columns: minmax(0, 206px) 118px;
    gap: 8px;
    align-items: start;
  }
  &__captcha-field {
    display: flex;
    align-items: center;
    min-height: 52px;
    border: 1px solid #aeb4be;
    border-radius: 6px;
    background: $white;
    padding: 0 14px;
    transition:
      border-color 0.2s ease,
      box-shadow 0.2s ease;
    &:focus-within {
      border-color: #6f9a78;
      box-shadow: 0 0 0 2px rgba(111, 154, 120, 0.12);
    }
    &.has-error {
      border-color: #dc3545;
    }
    input {
      width: 100%;
      border: 0;
      outline: 0;
      background: transparent;
      color: #22252a;
      font-size: 14px;
      &::placeholder {
        color: #9aa1ad;
      }
    }
  }
  &__captcha-image {
    width: 118px;
    height: 52px;
    border-radius: 6px;
    object-fit: cover;
    cursor: pointer;

    &.is-loading {
      opacity: 0.55;
      pointer-events: none;
    }
  }
  &__captcha-error {
    grid-column: 1 / -1;
    margin: -2px 0 0;
    color: #dc3545;
    font-size: 12px;
    line-height: 1.4;
  }
  &__success {
    width: 100%;
    min-height: 420px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 40px 0;
  }
  &__success-icon {
    width: 56px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;
    border-radius: 50%;
    background: #6f9273;
    color: $white;
    svg {
      width: 34px;
      height: 34px;
    }
    path {
      fill: none;
      stroke: currentColor;
      stroke-width: 3.6;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  }
  &__success-title {
    max-width: 760px;
    color: #6f9273;
    font-size: 22px;
    line-height: 1.45;
    font-weight: 700;
    margin-bottom: 20px;
    :is(p, h1, h2, h3, h4, h5, h6) {
      margin: 0;
      font: inherit;
      color: inherit;
    }
  }
  &__success-body {
    max-width: 760px;
    color: #45464a;
    font-size: 15px;
    line-height: 1.7;
    margin-bottom: 24px;
    :is(p, h1, h2, h3, h4, h5, h6) {
      margin: 0;
      font: inherit;
      color: inherit;
    }
    strong,
    b {
      color: #6f9273;
      font-weight: 700;
    }
  }
  &__success-button {
    min-width: 250px;
    min-height: 50px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #4d4d4d;
    border-radius: 4px;
    background: transparent;
    color: #45464a;
    font-size: 15px;
    line-height: 1.2;
    font-weight: 700;
    cursor: pointer;
    transition:
      background-color 0.2s ease,
      color 0.2s ease,
      border-color 0.2s ease;
    &:hover,
    &:focus-visible {
      border-color: #6f9273;
      background: #6f9273;
      color: $white;
      outline: none;
    }
  }
  @include tablet-landscape {
    padding: 72px 0;
    &__content {
      flex-direction: var(--flex-direction, row);
      gap: 40px;
    }
    &__content.has-right {
      flex-direction: row;
      align-items: flex-start;
      gap: 64px;
    }
    &__right {
      flex: 0 0 34%;
      min-width: 320px;
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
    &__side {
      text-align: var(--align, left);
    }
    &__success {
      min-height: 500px;
      padding: 56px 0;
    }
    &__success-icon {
      margin-bottom: 26px;
    }
    &__success-title {
      font-size: 24px;
      line-height: 1.5;
      margin-bottom: 20px;
    }
    &__success-body {
      font-size: 16px;
      margin-bottom: 24px;
    }
    &.row {
      #{$c}__content:not(.has-right) {
        gap: 80px;
      }
      #{$c}__content:not(.has-right) #{$c}__form-column {
        flex-direction: row;
        gap: 80px;
      }
      #{$c}__content:not(.has-right) #{$c}__side {
        align-self: flex-start;
        width: calc(50% - 40px);
      }
      #{$c}__content:not(.has-right) #{$c}__main {
        align-self: stretch;
        width: calc(50% - 40px);
      }
      #{$c}__content.has-right #{$c}__form-column {
        flex: 1 1 auto;
        min-width: 0;
      }
    }
  }
}
</style>
